/* @flow */
import { Map, List, fromJS } from 'immutable';
import type { Action } from 'trustar/utils/redux-types.js';
import { calculateDayRange } from 'trustar/utils/dashboard.js';

import {
  SET_DATE_OPTIONS,
  SELECT_ENCLAVE,
  SET_ENCLAVE_MENU,
  PAGINATE,
  PAGINATE_SUCCESS,
  PAGINATE_FAILURE,
  FETCH_TIME_RANGE,
  FETCH_TIME_RANGE_SUCCESS,
  FETCH_TIME_RANGE_FAILURE,
  SET_VIEW_OPTIONS,
  SAVE_DATE_RANGE,
  SAVE_DEFAULT_ENCLAVE,
} from './action-types.js';
import { generateDefaultRange } from './epics.js';

/* Data for each block of insights */
const defaultInsightsState = {
  data: List(),
  status: null,
  from: null,
  to: null,
  range: '6 Months',
  loading: 'all',
  elementCount: 0,
  pageNumber: 0,
  pageSize: 10,
  totalElements: 50,
};

export const defaultTimeRange = fromJS({
  category: null,
  showCustom: false,
  dateSince: null,
  dateUntil: null,
  daysBack: null,
});

/* Overall store shape */
export const initialState = fromJS({
  enclaveCorrelations: defaultInsightsState,
  communityCorrelations: defaultInsightsState,
  enclaveIoCs: defaultInsightsState,
  communityTrendingIocs: defaultInsightsState,
  malware: defaultInsightsState,
  cves: defaultInsightsState,
  meta: {
    view: {
      category: 'enclaveCorrelations',
      panel: 'insights',
    },
    timeRange: defaultTimeRange,
    savedRanges: {
      insights: { ...generateDefaultRange() },
      trends: { ...generateDefaultRange() },
    },
    enclaveSelection: {
      menuOpen: false,
      enclaveId: null,
      enclaveName: null,
      defaultEnclaveName: null,
    }
  },
});

export default (state: Map<any, any> = initialState, action: Action<*>) => {
  const { type, payload } = action;
  switch (type) {
  case FETCH_TIME_RANGE_SUCCESS:
    // TODO: Move this capitalization logic for CVEs to the server for all IOC queries
    let data = payload.data.get('data');
    if (payload.category === 'cves') {
      data = data.map(v => v.update('title', t => t.toUpperCase()));
    }
    return state
      .update(payload.category, s => s
        .set('loading', false)
        .merge(payload.data)
        .set('data', data));
  case FETCH_TIME_RANGE_FAILURE:
    return state
      .update(payload.category, s => s
        .set('loading', 'error'));
  case SET_DATE_OPTIONS:
    return state
      .mergeIn(['meta', 'timeRange'], payload)
      .setIn(['meta', 'enclaveSelection', 'menuOpen'], false);
  case PAGINATE:
    return state
      .update(payload.category, s => s.set('loading', 'pagination'))
      .setIn(['meta', 'timeRange'], defaultTimeRange);
  case PAGINATE_SUCCESS:
    return state
      .update(payload.category, s => s
        .set('loading', false)
        .update('data', d => d.concat(payload.data))
        .merge(payload.meta));
  case PAGINATE_FAILURE:
    return state
      .update(payload.category, s => s
        .set('loading', false));
  case FETCH_TIME_RANGE:
    const { category, from, to } = payload;
    const dateRange = calculateDayRange(from, to);
    return state
      .setIn(['meta', 'timeRange'], defaultTimeRange)
      .update(category, s => s
        .set('to', to)
        .set('from', from)
        .set('range', dateRange)
        .set('loading', 'all'));
  case SAVE_DEFAULT_ENCLAVE:
    const { id, name } = payload;
    return state
      .setIn(['meta', 'enclaveSelection', 'enclaveId'], id)
      .setIn(['meta', 'enclaveSelection', 'enclaveName'], name)
      .setIn(['meta', 'enclaveSelection', 'defaultEnclaveName'], name);
  case SET_ENCLAVE_MENU:
    return state
      .setIn(['meta', 'timeRange'], defaultTimeRange)
      .setIn(['meta', 'enclaveSelection', 'menuOpen'], action.payload);
  case SELECT_ENCLAVE:
    return state
      .setIn(['meta', 'enclaveSelection', 'menuOpen'], false)
      .setIn(['meta', 'enclaveSelection', 'enclaveId'], payload.id)
      .setIn(['meta', 'enclaveSelection', 'enclaveName'], payload.name);
  case SET_VIEW_OPTIONS:
    return state
      .mergeIn(['meta', 'view'], payload);
  case SAVE_DATE_RANGE: {
    const { category, from, to } = payload;
    return state.mergeIn(['meta', 'savedRanges', category], fromJS({ from, to }));
  }
  default: return state;
  }
};



// WEBPACK FOOTER //
// ./src/app/modules/insights/stores.js